// src/Pages/Bookings/BookingDetails.tsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import { GetApiMethod } from "../../Utils/ApiService";
import BoxHeader from "../../Components/Common/Box/Boxheader";

const BookingDetails = () => {
  const { id } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await GetApiMethod(`booking/${id}`);
        if (response?.data?.status === "success") {
          setBooking(response.data.data);
        } else {
          setError("Failed to fetch booking details.");
        }
      } catch (err) {
        console.error(err);
        setError("Something went wrong while fetching booking.");
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  const getStatusDotColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return "#28a745";
      case "pending":
        return "#FFA500";
      case "rejected":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  return (
    <AuthorizedLayout>
      <Box sx={{ px: 3, py: 2 }}>
        <Card className="card-layout">
          {/* Header */}
          <Box display="flex" alignItems="center" mb={2}>
          <BoxHeader dashboard_name="Bookings" tabs={["All Bookings"]} activeTab={0} />
          </Box>

          {/* Loading */}
          {loading && (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          )}

          {/* Error */}
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Content */}
          {!loading && booking && (
            <>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
              >
                <Tab label="Basic Details" />
                <Tab label="Services & Addons" />
                <Tab label="Bill Summary" />
              </Tabs>

              {/* Basic Details Tab */}
              {tabValue === 0 && (
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Booking ID</TableCell>
                      <TableCell>{booking.bookingNo}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Customer Name</TableCell>
                      <TableCell>{booking.bookingCustomer?.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Email ID</TableCell>
                      <TableCell>{booking.bookingCustomer?.email}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Phone Number</TableCell>
                      <TableCell>{booking.bookingCustomer?.phone}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Category</TableCell>
                      <TableCell>{booking.bookingCategory?.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Status</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: getStatusDotColor(booking.status),
                              mr: 1,
                            }}
                          />
                          {booking.status}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Booked On</TableCell>
                      <TableCell>{booking.createdAt}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Event Date & Time</TableCell>
                      <TableCell>
                        {booking.eventDate} , {booking.eventTime}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Event Location</TableCell>
                      <TableCell>{booking.eventLocation}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Provider</TableCell>
                      <TableCell>{booking.bookingProvider?.providerName}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}

              {/* Services & Addons Tab */}
             {tabValue === 1 && (
  <Table>
    <TableBody>
      {/* Services Section */}
      <TableRow>
        <TableCell colSpan={3} sx={{ fontWeight: 600, fontSize: "16px" }}>
          Services
        </TableCell>
         {/* <TableCell colSpan={3} sx={{ fontWeight: 600, fontSize: "16px" }}>
          Type
        </TableCell> */}
      </TableRow>

      {booking.bookingServices?.filter((s: any) => s.type === "Services").length > 0 ? (
        booking.bookingServices
          .filter((s: any) => s.type === "Services")
          .map((service: any, index: number) => (
            <TableRow key={`service-${index}`}>
              <TableCell>{service.name}</TableCell>
              <TableCell>1hr</TableCell>
              {/* <TableCell>{service.status === 0 ? "Inactive" : "Active"}</TableCell> */}
            </TableRow>
          ))
      ) : (
        <TableRow>
          <TableCell colSpan={3}>No services available.</TableCell>
        </TableRow>
      )}

      {/* Addons Section */}
      <TableRow>
        <TableCell colSpan={3} sx={{ fontWeight: 600, fontSize: "16px", pt: 3 }}>
          Addons
        </TableCell>
         {/* <TableCell colSpan={3} sx={{ fontWeight: 600, fontSize: "16px" }}>
          Type
        </TableCell> */}
      </TableRow>

      {booking.bookingServices?.filter((s: any) => s.type === "Addons").length > 0 ? (
        booking.bookingServices
          .filter((s: any) => s.type === "Addons")
          .map((addon: any, index: number) => (
            <TableRow key={`addon-${index}`}>
              <TableCell>{addon.name}</TableCell>
              <TableCell>2persons</TableCell>
              {/* <TableCell>{addon.status === 0 ? "Inactive" : "Active"}</TableCell> */}
            </TableRow>
          ))
      ) : (
        <TableRow>
          <TableCell colSpan={3}>No addons available.</TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
)}
              {/* Bill Summary Tab */}
             {tabValue === 2 && booking?.billSummary?.length > 0 && (
  <Table>
    <TableBody>
      {booking.billSummary.map((item: any, index: number) => (
        <TableRow key={`bill-${index}`}>
          <TableCell sx={{ fontWeight: 500 }}>{item.key}</TableCell>
          <TableCell>{item.value}</TableCell>
        </TableRow>
      ))}

      {/* Optionally, you can calculate and show total if needed */}
      <TableRow>
        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
        <TableCell>
          {booking.billSummary
            .reduce((total: number, curr: any) => {
              const value = parseFloat(curr.value.replace(/[^0-9.-]+/g, ""));
              return total + (isNaN(value) ? 0 : value);
            }, 0)
            .toLocaleString("en-IN", { style: "currency", currency: "INR" })}
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
)}

            </>
          )}
        </Card>
      </Box>
    </AuthorizedLayout>
  );
};

export default BookingDetails;
